import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  Timestamp,
  increment
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject,
  uploadBytesResumable,
  UploadTask
} from 'firebase/storage';
import { db, storage } from '../config/firebase.config';
import { 
  ProjectFile, 
  MoldFile, 
  FileUploadData,
  getFileCategoryFromExtension,
  canGenerateThumbnail,
  MAX_FILE_SIZE,
  ALLOWED_FILE_TYPES
} from '../types/file.types';
import { getCategoryFolder, createSafeFolderName } from '../utils/nas-path.utils';
import unifiedStorageService from './storage/unified-storage.service';

/**
 * 파일 업로드 에러
 */
export class FileUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FileUploadError';
  }
}

/**
 * 파일 업로드 진행률 콜백
 */
export type UploadProgressCallback = (progress: number) => void;

/**
 * 파일 확장자 추출
 */
function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

/**
 * 파일 유효성 검사
 * 크기만 검사 - 모든 확장자 허용 (미지원 확장자는 '기타' 카테고리)
 */
function validateFile(file: File): void {
  // 파일 크기 검사
  if (file.size > MAX_FILE_SIZE) {
    throw new FileUploadError(
      `파일 크기가 너무 큽니다. 최대 ${MAX_FILE_SIZE / 1024 / 1024}MB까지 가능합니다.`
    );
  }
  
  // 파일 타입 검사 제거 - 모든 확장자 허용
  // 미지원 확장자는 getFileCategoryFromExtension에서 '기타'로 자동 분류
}

/**
 * 썸네일 생성 (이미지 파일)
 */
async function generateImageThumbnail(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      // 썸네일 크기 (최대 500x500)
      const maxSize = 500;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('썸네일 생성 실패'));
        }
      }, 'image/jpeg', 0.8);
    };

    img.onerror = () => reject(new Error('이미지 로드 실패'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * 썸네일 생성 및 업로드
 * 
 * @note 썸네일은 시스템용이므로 항상 Firebase Storage에만 저장
 *       NAS에는 원본 파일만 저장
 * @param file 원본 파일
 * @param parentType 부모 타입 (project 또는 mold)
 * @param folderName 폴더명 (projectNumber_projectName 또는 moldNumber_moldName)
 */
async function uploadThumbnail(
  file: File,
  parentType: 'project' | 'mold',
  folderName: string
): Promise<string | undefined> {
  const fileType = getFileExtension(file.name);
  
  if (!canGenerateThumbnail(fileType)) {
    return undefined;
  }

  try {
    // 이미지 썸네일 생성
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileType)) {
      const thumbnailBlob = await generateImageThumbnail(file);
      const thumbnailPath = `${parentType}s/${folderName}/thumbnails/${Date.now()}_thumb.jpg`;
      
      // 썸네일은 Firebase Storage에만 저장 (시스템용)
      const thumbnailRef = ref(storage, thumbnailPath);
      await uploadBytes(thumbnailRef, thumbnailBlob);
      const thumbnailUrl = await getDownloadURL(thumbnailRef);
      
      return thumbnailUrl;
    }

    // PDF 썸네일은 Phase 2에서 구현
    // TODO: PDF 첫 페이지 썸네일 생성
    
    return undefined;
  } catch (error) {
    console.error('썸네일 생성 실패:', error);
    return undefined;
  }
}

/**
 * 프로젝트 파일 업로드
 */
export async function uploadProjectFile(
  projectId: string,
  uploadData: FileUploadData,
  userId: string,
  userName: string,
  projectName?: string,
  onProgress?: UploadProgressCallback
): Promise<ProjectFile> {
  const { file, category, description, tags, version } = uploadData;

  // 파일 유효성 검사
  validateFile(file);

  try {
    // 자동 카테고리 분류 (수동 선택이 없을 경우)
    const fileCategory = category || getFileCategoryFromExtension(file.name);
    
    // 폴더명 생성 (projectName이 이미 "번호_이름" 형태)
    const folderName = projectName 
      ? createSafeFolderName(projectName)
      : projectId;
    
    // Storage 경로 생성 (카테고리별 폴더)
    const fileExt = getFileExtension(file.name);
    const timestamp = Date.now();
    const categoryFolder = getCategoryFolder(fileCategory);
    const storagePath = `projects/${folderName}/${categoryFolder}/${timestamp}_${file.name}`;
    
    // 사람이 읽기 쉬운 경로
    const humanReadablePath = `projects/${folderName}/${categoryFolder}/${file.name}`;

    // Unified Storage를 통한 파일 업로드 (듀얼 스토리지)
    const fileUrl = await unifiedStorageService.uploadFile(storagePath, file);
    
    // 스토리지 설정 확인
    const config = await unifiedStorageService.getCurrentConfig();
    const isHybrid = config?.mode === 'hybrid';
    const masterStorage: 'firebase' | 'nas' = isHybrid ? 'nas' : 'firebase';

    // 썸네일 생성 및 업로드 (폴더명 사용)
    const thumbnailUrl = await uploadThumbnail(file, 'project', folderName);

    // Firestore 메타데이터 생성
    const fileData: Omit<ProjectFile, 'id'> = {
      fileName: file.name,
      fileType: fileExt,
      category: fileCategory,
      
      // 저장 위치 (듀얼 스토리지)
      storageType: isHybrid ? 'nas' : 'firebase',
      masterStorage,
      storagePath,
      fileUrl,
      humanReadablePath,
      ...(isHybrid && { nasPath: storagePath }), // 하이브리드면 NAS 경로 저장
      ...(isHybrid && file.size <= 10 * 1024 * 1024 && { firebasePath: storagePath }), // 10MB 이하면 캐시 경로 저장
      
      fileSize: file.size,
      mimeType: file.type,
      
      ...(thumbnailUrl && { thumbnailUrl }), // undefined인 경우 필드 제외
      hasThumbnail: !!thumbnailUrl,
      
      uploadedAt: Timestamp.now(),
      uploadedBy: userId,
      uploadedByName: userName,
      
      searchTerms: [file.name, fileCategory, ...(tags || [])],
      ...(tags && tags.length > 0 && { tags }), // undefined/빈 배열인 경우 필드 제외
      ...(description && { description }), // undefined/빈 문자열인 경우 필드 제외
      version: version || 'v1.0',
      isLatest: true,
      
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    // Firestore에 저장
    const filesRef = collection(db, 'projects', projectId, 'files');
    const docRef = await addDoc(filesRef, fileData);

    // 프로젝트 문서의 파일 카운터 업데이트
    const projectRef = doc(db, 'projects', projectId);
    await updateDoc(projectRef, {
      hasFiles: true,
      fileCount: increment(1),
      updatedAt: Timestamp.now()
    });

    return {
      id: docRef.id,
      ...fileData
    };
  } catch (error: any) {
    console.error('파일 업로드 실패:', error);
    throw new FileUploadError(error.message || '파일 업로드 중 오류가 발생했습니다.');
  }
}

/**
 * 금형 파일 업로드
 */
export async function uploadMoldFile(
  moldId: string,
  uploadData: FileUploadData,
  userId: string,
  userName: string,
  moldName?: string,
  onProgress?: UploadProgressCallback
): Promise<MoldFile> {
  const { file, category, description, tags, version } = uploadData;

  validateFile(file);

  try {
    // 자동 카테고리 분류 (수동 선택이 없을 경우)
    const fileCategory = category || getFileCategoryFromExtension(file.name);
    
    // 폴더명 생성 (moldName이 이미 "번호_이름" 형태)
    const folderName = moldName 
      ? createSafeFolderName(moldName)
      : moldId;
    
    // Storage 경로 생성 (카테고리별 폴더)
    const fileExt = getFileExtension(file.name);
    const timestamp = Date.now();
    const categoryFolder = getCategoryFolder(fileCategory);
    const storagePath = `molds/${folderName}/${categoryFolder}/${timestamp}_${file.name}`;
    
    // 사람이 읽기 쉬운 경로
    const humanReadablePath = `molds/${folderName}/${categoryFolder}/${file.name}`;

    // Unified Storage를 통한 파일 업로드 (듀얼 스토리지)
    const fileUrl = await unifiedStorageService.uploadFile(storagePath, file);
    
    // 스토리지 설정 확인
    const config = await unifiedStorageService.getCurrentConfig();
    const isHybrid = config?.mode === 'hybrid';
    const masterStorage: 'firebase' | 'nas' = isHybrid ? 'nas' : 'firebase';

    const thumbnailUrl = await uploadThumbnail(file, 'mold', folderName);

    // Firestore 메타데이터 생성
    const fileData: Omit<MoldFile, 'id'> = {
      fileName: file.name,
      fileType: fileExt,
      category: fileCategory,
      
      // 저장 위치 (듀얼 스토리지)
      storageType: isHybrid ? 'nas' : 'firebase',
      masterStorage,
      storagePath,
      fileUrl,
      humanReadablePath,
      ...(isHybrid && { nasPath: storagePath }), // 하이브리드면 NAS 경로 저장
      ...(isHybrid && file.size <= 10 * 1024 * 1024 && { firebasePath: storagePath }), // 10MB 이하면 캐시 경로 저장
      
      fileSize: file.size,
      mimeType: file.type,
      
      ...(thumbnailUrl && { thumbnailUrl }), // undefined인 경우 필드 제외
      hasThumbnail: !!thumbnailUrl,
      
      uploadedAt: Timestamp.now(),
      uploadedBy: userId,
      uploadedByName: userName,
      
      searchTerms: [file.name, fileCategory, ...(tags || [])],
      ...(tags && tags.length > 0 && { tags }), // undefined/빈 배열인 경우 필드 제외
      ...(description && { description }), // undefined/빈 문자열인 경우 필드 제외
      version: version || 'v1.0',
      isLatest: true,
      
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const filesRef = collection(db, 'molds', moldId, 'files');
    const docRef = await addDoc(filesRef, fileData);

    // 금형 문서의 파일 카운터 업데이트
    const moldRef = doc(db, 'molds', moldId);
    await updateDoc(moldRef, {
      hasFiles: true,
      fileCount: increment(1),
      updatedAt: Timestamp.now()
    });

    return {
      id: docRef.id,
      ...fileData
    };
  } catch (error: any) {
    console.error('파일 업로드 실패:', error);
    throw new FileUploadError(error.message || '파일 업로드 중 오류가 발생했습니다.');
  }
}

/**
 * 프로젝트 파일 목록 조회
 */
export async function getProjectFiles(projectId: string): Promise<ProjectFile[]> {
  try {
    const filesRef = collection(db, 'projects', projectId, 'files');
    const q = query(filesRef, orderBy('uploadedAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ProjectFile));
  } catch (error) {
    console.error('파일 목록 조회 실패:', error);
    throw error;
  }
}

/**
 * 금형 파일 목록 조회
 */
export async function getMoldFiles(moldId: string): Promise<MoldFile[]> {
  try {
    const filesRef = collection(db, 'molds', moldId, 'files');
    const q = query(filesRef, orderBy('uploadedAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as MoldFile));
  } catch (error) {
    console.error('파일 목록 조회 실패:', error);
    throw error;
  }
}

/**
 * 특정 파일 조회
 */
export async function getFileById(
  parentType: 'project' | 'mold',
  parentId: string,
  fileId: string
): Promise<ProjectFile | MoldFile | null> {
  try {
    const collectionPath = parentType === 'project' 
      ? `projects/${parentId}/files`
      : `molds/${parentId}/files`;
    
    const fileRef = doc(db, collectionPath, fileId);
    const fileSnap = await getDoc(fileRef);

    if (fileSnap.exists()) {
      return {
        id: fileSnap.id,
        ...fileSnap.data()
      } as ProjectFile | MoldFile;
    }

    return null;
  } catch (error) {
    console.error('파일 조회 실패:', error);
    throw error;
  }
}

/**
 * 카테고리별 파일 조회
 */
export async function getFilesByCategory(
  parentType: 'project' | 'mold',
  parentId: string,
  category: string
): Promise<(ProjectFile | MoldFile)[]> {
  try {
    const collectionPath = parentType === 'project'
      ? `projects/${parentId}/files`
      : `molds/${parentId}/files`;
    
    const filesRef = collection(db, collectionPath);
    const q = query(
      filesRef,
      where('category', '==', category),
      orderBy('uploadedAt', 'desc')
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ProjectFile | MoldFile));
  } catch (error) {
    console.error('카테고리별 파일 조회 실패:', error);
    throw error;
  }
}

/**
 * 파일 삭제
 */
export async function deleteFile(
  parentType: 'project' | 'mold',
  parentId: string,
  fileId: string
): Promise<void> {
  try {
    // Firestore에서 파일 메타데이터 조회
    const file = await getFileById(parentType, parentId, fileId);
    if (!file) {
      throw new Error('파일을 찾을 수 없습니다.');
    }

    // Storage에서 파일 삭제
    const fileRef = ref(storage, file.storagePath);
    await deleteObject(fileRef);

    // 썸네일도 삭제
    if (file.thumbnailUrl && file.hasThumbnail) {
      try {
        // thumbnailUrl에서 경로 추출하여 삭제
        const thumbnailPath = file.storagePath.replace('/files/', '/thumbnails/').replace(/\.[^/.]+$/, '_thumb.jpg');
        const thumbnailRef = ref(storage, thumbnailPath);
        await deleteObject(thumbnailRef);
      } catch (error) {
        console.warn('썸네일 삭제 실패 (무시):', error);
      }
    }

    // Firestore에서 메타데이터 삭제
    const collectionPath = parentType === 'project'
      ? `projects/${parentId}/files`
      : `molds/${parentId}/files`;
    
    await deleteDoc(doc(db, collectionPath, fileId));

    // 부모 문서의 파일 카운터 업데이트
    const parentPath = parentType === 'project' 
      ? `projects/${parentId}`
      : `molds/${parentId}`;
    
    const parentRef = doc(db, parentPath);
    await updateDoc(parentRef, {
      fileCount: increment(-1),
      updatedAt: Timestamp.now()
    });

    // fileCount가 0이면 hasFiles를 false로 변경
    const parentDoc = await getDoc(parentRef);
    if (parentDoc.exists() && parentDoc.data().fileCount <= 0) {
      await updateDoc(parentRef, {
        hasFiles: false,
        fileCount: 0
      });
    }

  } catch (error: any) {
    console.error('파일 삭제 실패:', error);
    throw new Error(error.message || '파일 삭제 중 오류가 발생했습니다.');
  }
}

/**
 * 파일 다운로드 URL 가져오기
 */
export async function getFileDownloadUrl(
  parentType: 'project' | 'mold',
  parentId: string,
  fileId: string
): Promise<string> {
  try {
    const file = await getFileById(parentType, parentId, fileId);
    
    if (!file) {
      throw new Error('파일을 찾을 수 없습니다.');
    }

    // Firebase Storage URL이 있으면 바로 반환
    if (file.fileUrl) {
      return file.fileUrl;
    }

    // Phase 2: NAS 파일인 경우
    if (file.storageType === 'nas' && file.nasPath) {
      // TODO: NAS 파일 다운로드 처리
      throw new Error('NAS 파일 다운로드는 Phase 2에서 지원됩니다.');
    }

    throw new Error('파일 URL을 찾을 수 없습니다.');
  } catch (error: any) {
    console.error('파일 URL 조회 실패:', error);
    throw error;
  }
}

/**
 * 파일 메타데이터 업데이트
 */
export async function updateFileMetadata(
  parentType: 'project' | 'mold',
  parentId: string,
  fileId: string,
  updates: Partial<Pick<ProjectFile, 'description' | 'tags' | 'version' | 'isLatest'>>
): Promise<void> {
  try {
    const collectionPath = parentType === 'project'
      ? `projects/${parentId}/files`
      : `molds/${parentId}/files`;
    
    const fileRef = doc(db, collectionPath, fileId);
    
    await updateDoc(fileRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('파일 메타데이터 업데이트 실패:', error);
    throw error;
  }
}

/**
 * 파일 검색 (파일명 기반)
 */
export async function searchFiles(
  parentType: 'project' | 'mold',
  parentId: string,
  searchKeyword: string
): Promise<(ProjectFile | MoldFile)[]> {
  try {
    const collectionPath = parentType === 'project'
      ? `projects/${parentId}/files`
      : `molds/${parentId}/files`;
    
    const filesRef = collection(db, collectionPath);
    const q = query(
      filesRef,
      where('searchTerms', 'array-contains', searchKeyword.toLowerCase()),
      orderBy('uploadedAt', 'desc')
    );
    
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as ProjectFile | MoldFile));
  } catch (error) {
    console.error('파일 검색 실패:', error);
    throw error;
  }
}

/**
 * 전체 파일 통계 (프로젝트별)
 */
export async function getProjectFileStats(projectId: string) {
  try {
    const files = await getProjectFiles(projectId);
    
    const stats = {
      totalFiles: files.length,
      totalSize: files.reduce((sum, file) => sum + file.fileSize, 0),
      byCategory: {} as Record<string, number>,
      byType: {} as Record<string, number>,
    };

    files.forEach(file => {
      // 카테고리별
      stats.byCategory[file.category] = (stats.byCategory[file.category] || 0) + 1;
      
      // 파일 타입별
      stats.byType[file.fileType] = (stats.byType[file.fileType] || 0) + 1;
    });

    return stats;
  } catch (error) {
    console.error('파일 통계 조회 실패:', error);
    throw error;
  }
}

/**
 * 프로젝트 파일 삭제
 */
export async function deleteProjectFile(projectId: string, fileId: string): Promise<void> {
  return deleteFile('project', projectId, fileId);
}

/**
 * 금형 파일 삭제
 */
export async function deleteMoldFile(moldId: string, fileId: string): Promise<void> {
  return deleteFile('mold', moldId, fileId);
}

/**
 * 파일 즐겨찾기 토글
 */
export async function toggleFileFavorite(
  parentType: 'project' | 'mold',
  parentId: string,
  fileId: string,
  isFavorite: boolean
): Promise<void> {
  try {
    const collectionPath = parentType === 'project'
      ? `projects/${parentId}/files`
      : `molds/${parentId}/files`;
    
    const fileRef = doc(db, collectionPath, fileId);
    
    await updateDoc(fileRef, {
      isFavorite,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('즐겨찾기 업데이트 실패:', error);
    throw error;
  }
}

/**
 * 파일 정보 업데이트 (파일명, 설명, 태그, 중요도)
 */
export interface FileUpdateData {
  fileName?: string;
  description?: string;
  tags?: string[];
  importance?: 'low' | 'medium' | 'high';
}

export async function updateFileInfo(
  parentType: 'project' | 'mold',
  parentId: string,
  fileId: string,
  data: FileUpdateData
): Promise<void> {
  try {
    const collectionPath = parentType === 'project'
      ? `projects/${parentId}/files`
      : `molds/${parentId}/files`;
    
    const fileRef = doc(db, collectionPath, fileId);
    
    // 업데이트할 필드만 추출
    const updates: any = {
      updatedAt: Timestamp.now()
    };
    
    if (data.fileName !== undefined) {
      updates.fileName = data.fileName;
      // 파일명 변경 시 searchTerms도 업데이트
      const searchTerms = [data.fileName.toLowerCase()];
      if (data.tags) {
        searchTerms.push(...data.tags.map(tag => tag.toLowerCase()));
      }
      updates.searchTerms = searchTerms;
    }
    
    if (data.description !== undefined) {
      updates.description = data.description;
    }
    
    if (data.tags !== undefined) {
      updates.tags = data.tags;
      // 태그 변경 시 searchTerms도 업데이트
      if (!updates.searchTerms) {
        const currentFile = await getFileById(parentType, parentId, fileId);
        if (currentFile) {
          updates.searchTerms = [
            currentFile.fileName.toLowerCase(),
            ...data.tags.map(tag => tag.toLowerCase())
          ];
        }
      }
    }
    
    if (data.importance !== undefined) {
      updates.importance = data.importance;
    }
    
    await updateDoc(fileRef, updates);
  } catch (error) {
    console.error('파일 정보 업데이트 실패:', error);
    throw error;
  }
}
